/**
 * 调度中心主页 · 纯展示辅助（TAOTUAN-3）。
 * 这里只有「怎么画」：日期文案、状态文案、颜色档位、深链解析。
 * 红线：可订 / 超卖 / 余位 / 保本 / 车档兼容等业务数字一律来自接口；本文件不产生新的业务数。
 * cellTone 是唯一的"看两个数定颜色"处，口径取样稿 + API_MAP 第 4 版「临界黄灯：剩 ≤2 或 ≤15%」，只决定颜色，不输出数字。
 */
import type { BoardRun, RunState } from "@/services/types/dispatchBoard";

export const WEEKDAY_CN = ["", "一", "二", "三", "四", "五", "六", "日"];

/** 集合时间的时区标签：只做名称翻译，不做任何时区换算（集合时间本身就是当地时间）。 */
const TZ_CN: Record<string, string> = {
  "America/Los_Angeles": "太平洋", "America/Denver": "山地", "America/Phoenix": "亚利桑那",
  "America/Chicago": "中部", "America/New_York": "东部",
};
export const tzLabel = (tz: string | null | undefined): string => (tz ? TZ_CN[tz] ?? tz : "");

/** 太平洋时间今天（YYYY-MM-DD）——公司运营时区，仅用于决定默认窗口起点。 */
export function pacificToday(): string {
  return new Intl.DateTimeFormat("en-CA", { timeZone: "America/Los_Angeles" }).format(new Date());
}

export function addDays(ymd: string, n: number): string {
  const [y, m, d] = ymd.split("-").map(Number);
  const t = new Date(Date.UTC(y, m - 1, d + n));
  return t.toISOString().slice(0, 10);
}

/** "2026-10-04" → "10/4" */
export function fmtMD(ymd: string): string {
  const [, m, d] = ymd.split("-").map(Number);
  return `${m}/${d}`;
}

/** board.generated_at 已是太平洋时间 ISO（2026-10-02T10:32:11-07:00）→ "10:32"，不再做时区换算。 */
export function fmtGeneratedAt(iso: string | undefined): string {
  const m = iso?.match(/T(\d{2}:\d{2})/);
  return m ? m[1] : "—";
}

export type Tone = "ok" | "tight" | "over" | "miss";

/** 颜色档位：cap 缺 = 未录；超 = 红；剩余 ≤max(1,15%) = 黄；其余绿。仅着色。 */
export function cellTone(used: number, cap: number | null | undefined): Tone {
  if (cap == null) return "miss";
  if (used > cap) return "over";
  if (cap - used <= Math.max(1, Math.round(cap * 0.15))) return "tight";
  return "ok";
}

/** 总览「可订」格颜色：超卖 = 红；可订 ≤2 = 黄；其余绿（样稿 fmtSO 同口径，仅着色）。 */
export function sellableTone(sellable: number, oversold: number): "ok" | "tight" | "over" {
  if (oversold > 0) return "over";
  return sellable <= 2 ? "tight" : "ok";
}

export const RUN_STATE_TEXT: Record<RunState, string> = {
  draft: "草排",
  due: "待定排",
  locked: "已锁定",
  running: "进行中",
  done: "已结束",
  cancelled: "已取消",
};

export function runStateText(run: BoardRun): string {
  if (run.state === "locked" && run.driver) {
    if (run.driver.response === "accepted") return "已锁定 · 司导已接单";
    if (run.driver.response === "declined") return "已锁定 · 司导已拒单";
    return "已锁定 · 等司导接单";
  }
  return RUN_STATE_TEXT[run.state] ?? run.state;
}

/** 车次色块：A 蓝 / B 靛 / C 紫（样稿 .car 三档；盐湖城青色接口没给标记，暂不区分）。 */
export function carKind(tier: string | null): "" | "b" | "c" {
  const l = (tier ?? "").charAt(0).toUpperCase();
  return l === "B" ? "b" : l === "C" ? "c" : "";
}

export type FocusKind = "run_day" | "hotel_day" | "ticket_day";
export interface Focus { kind: FocusKind; id: string; date: string }

/** `focus=run_day:{id}:{date}` → 结构化；格式不对 = null（页面显示提示，不报错）。 */
export function parseFocus(raw: string | null): Focus | null {
  if (!raw) return null;
  const parts = raw.split(":");
  if (parts.length !== 3) return null;
  const [kind, id, date] = parts;
  if (kind !== "run_day" && kind !== "hotel_day" && kind !== "ticket_day") return null;
  if (!id || !/^\d{4}-\d{2}-\d{2}$/.test(date)) return null;
  return { kind, id, date };
}

/** 格子 DOM 键：车次格 r: / 酒店格 h: / 门票格 t:（高亮与深链共用）。 */
export const cellKey = {
  run: (runId: string, date: string) => `r:${runId}:${date}`,
  hotel: (hotelId: string, date: string) => `h:${hotelId}:${date}`,
  ticket: (slotId: string, date: string) => `t:${slotId}:${date}`,
};

export function focusCellKey(f: Focus): string {
  return f.kind === "run_day" ? cellKey.run(f.id, f.date) : f.kind === "hotel_day" ? cellKey.hotel(f.id, f.date) : cellKey.ticket(f.id, f.date);
}
